/**
 * « Programme » — la console LIT un chapitre du manuel vivant (chantier
 * « manuel vivant », Φ-MV-1, MV-CNT-06 : « lire un chapitre »).
 *
 * Deux écrans, une adresse chacun (même règle que l'administration : un lien
 * s'envoie, un retour arrière ramène où l'on était) :
 *
 *   /programme          les chapitres en base, publiés ou non
 *   /programme/:code    un chapitre — notions, prérequis, attentes, réemplois
 *
 * ─── Ce que cette page montre, que l'élève ne voit pas ──────────────────────
 *
 * Le savoir qui accueille chaque notion, sa part du temps de classe, et les
 * ATTENTES DU PROGRAMME qu'elle porte. La position face au programme est
 * l'affaire du tuteur (F-15) : la route de l'élève ne rend aucun de ces
 * champs, celle-ci (`GET /v1/tuteur/manuel/chapitres/:code`) les rend tous.
 *
 * ─── La charge de la page (F-11, MV-UX-02) ──────────────────────────────────
 *
 * UNE section ouverte à l'arrivée — les notions, ce pour quoi on vient. Les
 * quatre autres sont repliées, chacune avec un résumé qui dit ce qu'il y a
 * derrière. Toutes passent par `SectionRabattable`, qui retient le choix par
 * utilisateur : c'est son premier appelant.
 *
 * ─── Ce qu'elle ne fait pas ─────────────────────────────────────────────────
 *
 * Elle LIT le programme. Corriger un chapitre se fait dans son dossier
 * (`contenu/<matière>/<niveau>/<chapitre>/`), puis par l'import : la console
 * n'écrit ni notion ni prérequis — redécouper le programme à la main
 * déplacerait des mesures déjà écrites.
 *
 * ─── La base de savoir (Φ-MV-2) ─────────────────────────────────────────────
 *
 * Sous le programme, quatre sections de plus, repliées elles aussi : les
 * fiches, les capsules, les erreurs et leurs remèdes, et la publication du
 * chapitre — avec le compte de ce qui reste à relire. Elles vivent dans
 * `SavoirChapitre.tsx` ; relire une fiche, une capsule ou une erreur se fait
 * sur sa sous-page (`RelectureSavoirPages.tsx`). La console y écrit un ÉTAT
 * (relu, en service ou non), jamais un contenu. La relecture d'un exercice de
 * chapitre arrive avec la banque (Φ-MV-3).
 *
 * Aucun code interne à l'écran, aucun pourcentage : voir `lib/programme-format.ts`.
 */
import { denombrer, type ChapitreProgrammeResponse } from '@silex/shared';
import { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { api } from '../api/client';
import { Badge } from '../components/Badge';
import { EmptyState } from '../components/EmptyState';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { ListePaginee, Pagination, usePageDansAdresse } from '../components/Pagination';
import { SectionRabattable } from '../components/SectionRabattable';
import { EtiquetteSequence, SelecteurVoirComme } from '../components/SequenceMaths';
import { TexteMaths } from '../components/TexteMaths';
import { adresseRelectureExercices } from '../lib/contenus-format';
import { manuelVivantActif } from '../lib/manuel-vivant';
import { paginer } from '../lib/pagination';
import {
  attenteAuNiveau,
  attentesDuChapitre,
  direJalon,
  niveauExerciceAttendu,
  nommerPrerequis,
  rangNotion,
  reemploisDuChapitre,
  resumesDesSections,
  seancesSurDix,
  situerAppui,
  direRessourcesDAppui,
  rangDuChapitre,
  situerChapitre,
  statutChapitre,
} from '../lib/programme-format';
import { formatDate } from '../lib/tuteur-format';
import { useAsync, useAsyncRechargeable } from '../lib/useAsync';

import { SavoirDuChapitre } from './SavoirChapitre';

/* ─────────────────────────────── la liste ─────────────────────────────── */

export function ProgrammePage({ titre = 'Programme' }: { readonly titre?: string }) {
  /* Le drapeau garde aussi l'adresse : éteint, la page n'existe pas — et elle
     n'émet aucune requête (un chargeur `null` laisse `useAsync` au repos). */
  const actif = manuelVivantActif();
  /* Mémorisé : `useAsync` relance dès que l'identité du chargeur change. */
  const loader = useMemo(() => (actif ? () => api.programmeChapitres() : null), [actif]);
  const liste = useAsync(loader);
  /* AB#880 : 73 chapitres en une colonne glissante — par pages de 20 désormais. */
  const { page, allerA, ancre } = usePageDansAdresse();

  if (!actif) return <Navigate to="/" replace />;

  const decoupe = liste.status === 'ready' ? paginer(liste.data.chapitres, page) : null;

  return (
    <div className="content__inner">
      <div className="dash-topbar" ref={ancre}>
        <div>
          <h1>{titre}</h1>
          <p className="page-sub">
            {liste.status === 'ready'
              ? denombrer(liste.data.chapitres.length, 'chapitre')
              : 'Les chapitres du manuel.'}
          </p>
        </div>
      </div>

      {liste.status === 'loading' ? <Chargement quoi="les chapitres" /> : null}
      {liste.status === 'error' ? <ErreurRessource message={liste.message} /> : null}

      {liste.status === 'ready' && liste.data.chapitres.length === 0 ? (
        <EmptyState title="Aucun chapitre en base">
          Un chapitre entre par l’import de son dossier de contenu. Tant qu’aucun n’est importé, le
          manuel de l’élève est vide lui aussi.
        </EmptyState>
      ) : null}

      {decoupe && decoupe.total > 0 ? (
        <>
          {/* AB#888 : les boutons aussi AU-DESSUS — sans dérouler 20 lignes. */}
          <Pagination
            page={decoupe}
            singulier="chapitre"
            onPage={allerA}
            testId="programme-pages-haut"
            enTete
          />
          <div className="list list--programme" data-testid="programme-chapitres">
            {decoupe.elements.map((c) => {
              const statut = statutChapitre(c.statut);
              return (
                <Link key={c.code} className="li li--link" to={`/programme/${c.code}`}>
                  <span className="li-main">
                    <span className="li-title">{c.titre}</span>
                    <span className="li-sub">
                      {[situerChapitre(c.matiere, c.niveau, c.nature), rangDuChapitre(c)]
                        .filter((x) => x !== null)
                        .join(' · ')}{' '}
                      · {c.nbNotions > 0 ? denombrer(c.nbNotions, 'notion') : 'notions à venir'}
                    </span>
                  </span>
                  <Badge tone={statut.tone} label={statut.label} />
                </Link>
              );
            })}
          </div>
          <Pagination
            page={decoupe}
            singulier="chapitre"
            onPage={allerA}
            testId="programme-pages"
          />
        </>
      ) : null}
    </div>
  );
}

/* ─────────────────────────────── un chapitre ─────────────────────────────── */

export function ChapitreProgrammePage() {
  const { code } = useParams<{ code: string }>();
  const actif = manuelVivantActif();
  const loader = useMemo(
    () => (actif && code ? () => api.programmeChapitre(code) : null),
    [actif, code],
  );
  /* Rechargeable : publier le chapitre ou le sortir du service (section
     « Publication ») change le statut que l'en-tête affiche. */
  const { resource: chapitre, recharger } = useAsyncRechargeable(loader);

  if (!actif) return <Navigate to="/" replace />;

  return (
    <div className="content__inner">
      <p className="note-line fil-retour">
        <Link to="/programme">← Tous les chapitres</Link>
      </p>
      {chapitre.status === 'loading' ? <Chargement quoi="le chapitre" /> : null}
      {chapitre.status === 'error' ? <ErreurRessource message={chapitre.message} /> : null}
      {chapitre.status === 'ready' ? (
        <Chapitre donnees={chapitre.data} surChangement={recharger} />
      ) : null}
    </div>
  );
}

function Chapitre({
  donnees,
  surChangement,
}: {
  donnees: ChapitreProgrammeResponse;
  surChangement: () => void;
}) {
  const { chapitre: c, notions, appuis, retirees } = donnees;
  const statut = statutChapitre(c.statut);
  const resumes = resumesDesSections(donnees);
  const attentes = attentesDuChapitre(notions);
  const reemplois = reemploisDuChapitre(notions);
  /* La mémoire des sections est PAR CHAPITRE, et par son code stable. */
  const cle = (section: string): string => `programme.${c.code}.${section}`;
  /* Quand toutes les notions remontent au MÊME savoir — le cas d'un chapitre
     rangé sous un seul savoir du programme —, on le dit une fois, sous le
     titre de la section, plutôt que six fois dans une colonne. */
  const savoirs = [...new Set(notions.map((n) => n.savoir.enonce))];
  const savoirUnique = savoirs.length === 1 ? savoirs[0] : undefined;
  const nommer = (liste: readonly { rang: number; titre: string }[]): string =>
    liste.map((n) => `${rangNotion(c.rang, n.rang)} ${n.titre}`).join(' · ');

  return (
    <>
      <div className="dash-topbar">
        <div>
          <p className="eyebrow">{situerChapitre(c.matiere, c.niveau, c.nature)}</p>
          <h1>{c.titre}</h1>
          <p className="page-sub">
            {rangDuChapitre(c) === null ? 'Hors table des matières' : `Chapitre ${c.rang}`} ·{' '}
            {resumes.notions} · version {c.version}, mise à jour le {formatDate(c.misAJourLe)}
            {c.sequences.length > 0 ? ` · séquences ${c.sequences.join(', ')}` : ''}
          </p>
        </div>
        <div className="row wrap">
          {/* AB#888 : « on déroule jusqu'à la fin pour les exercices » — la
              section « Exercices » est la 9e de la page ; la file s'ouvre
              d'ici, sans dérouler. */}
          <Link
            className="btn btn--sm"
            to={adresseRelectureExercices(c.code)}
            data-testid="programme-relire-exercices"
          >
            Relire les exercices
          </Link>
          {/* « Voir comme » (F-84) : un chapitre de 4e ou de 5e en mathématiques seulement. */}
          {c.matiere === 'mathematiques' && (c.niveau === 'sec4' || c.niveau === 'sec5') ? (
            <SelecteurVoirComme surChangement={surChangement} />
          ) : null}
          <Badge tone={statut.tone} label={statut.label} />
        </div>
      </div>

      <div className="stack" data-testid="programme-chapitre">
        {/* ── 1. Les notions — la seule section ouverte à l'arrivée ───────── */}
        <div data-testid="programme-notions">
          <SectionRabattable
            titre="Notions"
            resume={resumes.notions}
            sous={
              savoirUnique
                ? `Leur mesure remonte au savoir « ${savoirUnique} ».`
                : 'Dans l’ordre d’enseignement, telles que l’élève les lit.'
            }
            cleMemoire={cle('notions')}
          >
            {notions.length === 0 ? (
              <p className="note-line">Les notions de ce chapitre ne sont pas encore importées.</p>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th scope="col">Rang</th>
                      <th scope="col">Notion, et ce que l’élève saura faire</th>
                      {savoirUnique ? null : <th scope="col">Savoir qui reçoit la mesure</th>}
                      <th scope="col">Séances sur dix</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notions.map((n) => (
                      <tr key={n.code}>
                        <td className="data">{rangNotion(c.rang, n.rang)}</td>
                        <td>
                          <strong>{n.titre}</strong>{' '}
                          <EtiquetteSequence sequences={n.sequencesReservees} />
                          <span className="cell-sub cell-sub--lisible">
                            {n.capacite === '' ? (
                              'Capacité non rédigée.'
                            ) : (
                              <TexteMaths texte={n.capacite} />
                            )}
                          </span>
                        </td>
                        {savoirUnique ? null : <td>{n.savoir.enonce}</td>}
                        <td className="data">{seancesSurDix(n.partRythme) ?? 'non donné'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionRabattable>
        </div>

        {/* ── 2. Les prérequis : une relation entre notions ───────────────── */}
        <SectionRabattable
          titre="Prérequis"
          resume={resumes.prerequis}
          ouverteParDefaut={false}
          cleMemoire={cle('prerequis')}
        >
          <h4>Ce que chaque notion requiert</h4>
          <ListePaginee
            elements={notions}
            param="p-prerequis"
            singulier="notion"
            testId="programme-prerequis-liste"
          >
            {(page) => (
              <div className="list list--programme">
                {page.map((n) => (
                  <div key={n.code} className="li">
                    <span className="li-main">
                      <span className="li-title">
                        {rangNotion(c.rang, n.rang)} {n.titre}
                      </span>
                      <span className="li-sub">
                        {n.prerequis.length === 0
                          ? 'Ne requiert rien.'
                          : n.prerequis.map((p) => nommerPrerequis(p, c.rang)).join(' · ')}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </ListePaginee>
          {appuis.length > 0 ? (
            <>
              <h4>Les acquis d’avant sur lesquels le chapitre s’appuie</h4>
              <ListePaginee
                elements={appuis}
                param="p-appuis"
                singulier="acquis"
                pluriel="acquis"
                testId="programme-appuis-liste"
              >
                {(page) => (
                  <div className="list list--programme">
                    {page.map((p) => (
                      <div key={p.code} className="li">
                        <span className="li-main">
                          <span className="li-title">{p.titre}</span>
                          <span className="li-sub">
                            {p.capacite ? <TexteMaths texte={p.capacite} /> : null}
                          </span>
                          <span className="li-sub">{situerAppui(p)}</span>
                          {/* Un appui qu'un socle de prérequis partagés a doté d'un
                          contenu (`CAP-01`) : la console y mène. */}
                          {p.ressources ? (
                            <span className="li-sub">
                              <Link
                                to={`/programme/${p.ressources.chapitre}`}
                                data-testid={`programme-appui-${p.code}`}
                              >
                                {direRessourcesDAppui(p.ressources)}
                              </Link>
                            </span>
                          ) : null}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </ListePaginee>
            </>
          ) : null}
        </SectionRabattable>

        {/* ── 3. Les attentes du programme — pour le tuteur seulement (F-15) ── */}
        <SectionRabattable
          titre="Attentes du programme"
          resume={resumes.attentes}
          sous="Progression des apprentissages. L’élève ne voit rien de cette section."
          ouverteParDefaut={false}
          cleMemoire={cle('attentes')}
        >
          {attentes.length === 0 ? (
            <p className="note-line">Aucune attente n’est rattachée aux notions de ce chapitre.</p>
          ) : (
            <ListePaginee
              elements={attentes}
              param="p-attentes"
              singulier="attente"
              testId="programme-attentes-liste"
            >
              {(page) => (
                <div className="list list--programme">
                  {page.map(({ attente: a, notions: porteuses }) => {
                    const niveauAttendu = niveauExerciceAttendu(a.niveauExerciceAttendu);
                    const suite = a.trajectoire.filter((j) => j.niveau !== c.niveau);
                    return (
                      <div key={a.code} className="li">
                        <span className="li-main">
                          <span className="li-title">{a.enonce}</span>
                          <span className="li-sub">
                            {attenteAuNiveau(a.statutAuNiveau, c.niveau)}
                            {niveauAttendu ? ` · atteinte au ${niveauAttendu}` : ''} · page {a.page}{' '}
                            du programme
                          </span>
                          <span className="li-sub">Portée par : {nommer(porteuses)}</span>
                          {suite.length > 0 ? (
                            <span className="li-sub">
                              Ailleurs : {suite.map(direJalon).join(' ; ')}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </ListePaginee>
          )}
        </SectionRabattable>

        {/* ── 4. Où les notions resservent ─────────────────────────────────── */}
        <SectionRabattable
          titre="Réemplois"
          resume={resumes.reemplois}
          ouverteParDefaut={false}
          cleMemoire={cle('reemplois')}
        >
          {reemplois.length === 0 ? (
            <p className="note-line">Aucun réemploi n’est décrit pour ce chapitre.</p>
          ) : (
            <ListePaginee
              elements={reemplois}
              param="p-reemplois"
              singulier="réemploi"
              testId="programme-reemplois-liste"
            >
              {(page) => (
                <div className="list list--programme">
                  {page.map((r) => (
                    <div key={`${r.ou}|${r.niveau}`} className="li">
                      <span className="li-main">
                        <span className="li-title">{r.ou}</span>
                        <span className="li-sub">
                          {r.niveau} · {nommer(r.notions)}
                        </span>
                        {r.relie ? null : (
                          <span className="li-sub">Non relié à un savoir de la banque.</span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </ListePaginee>
          )}
        </SectionRabattable>

        {/* ── 5. Ce que le chapitre a remplacé ─────────────────────────────── */}
        {retirees.length > 0 ? (
          <SectionRabattable
            titre="Notions du corpus remplacées"
            resume={resumes.retirees}
            sous="Sorties du service, jamais supprimées : les mesures déjà écrites y tiennent."
            ouverteParDefaut={false}
            cleMemoire={cle('retirees')}
          >
            <ListePaginee
              elements={retirees}
              param="p-retirees"
              singulier="notion remplacée"
              pluriel="notions remplacées"
              testId="programme-retirees-liste"
            >
              {(page) => (
                <div className="list list--programme">
                  {page.map((r) => (
                    <div key={r.code} className="li">
                      <span className="li-main">
                        <span className="li-title">{r.titre}</span>
                        <span className="li-sub">Retirée le {formatDate(r.retireeLe)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </ListePaginee>
          </SectionRabattable>
        ) : null}

        {/* ── 6 à 9. La base de savoir : fiches, capsules, erreurs, publication ── */}
        <SavoirDuChapitre
          chapitre={{ code: c.code, rang: c.rang, statut: c.statut, nbNotions: notions.length }}
          surChangementDuChapitre={surChangement}
        />
      </div>
    </>
  );
}
